import { LoaderService } from '@/app/common/services/loader/loader.service'
import { AsyncPipe, CommonModule } from '@angular/common'
import { HttpErrorResponse } from '@angular/common/http'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { BehaviorSubject } from 'rxjs'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

import { NzButtonModule } from 'ng-zorro-antd/button'
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox'
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker'
import { NzDropDownModule } from 'ng-zorro-antd/dropdown'
import { NzEmptyModule } from 'ng-zorro-antd/empty'
import { NzIconModule } from 'ng-zorro-antd/icon'
import { NzInputModule } from 'ng-zorro-antd/input'
import { NzModalService } from 'ng-zorro-antd/modal'
import {
  NzNotificationModule,
  NzNotificationService,
} from 'ng-zorro-antd/notification'
import { NzPaginationModule } from 'ng-zorro-antd/pagination'
import { NzTableFilterList, NzTableModule } from 'ng-zorro-antd/table'
import { NzTagModule } from 'ng-zorro-antd/tag'

import { HeaderService } from '@/app/common/services/header/header.service'
import { AuthService } from '@/app/modules/auth/core/service'
import { ChecklistHeaderService } from '@/app/modules/checklist-header/core/service'
import { ChecklistHeaderFindAllResponse } from '@/app/modules/checklist-header/core/type'
import { CompanyService } from '@/app/modules/company/core/service'
import { ImagesService } from '@/app/modules/images/core/service'
import { User } from '@/app/modules/user/core/type'

type MaintenanceStatus =
  | 'in_progress'
  | 'done'
  | 'canceled'
  | 'not_proceed'

@Component({
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    CommonModule,
    FormsModule,
    RouterModule,
    NzButtonModule,
    NzCheckboxModule,
    NzDatePickerModule,
    NzDropDownModule,
    NzEmptyModule,
    NzIconModule,
    NzInputModule,
    NzNotificationModule,
    NzPaginationModule,
    NzTableModule,
    NzTagModule,
  ],
  providers: [NzModalService],
})
export class ListMaintenanceComponent implements OnInit, OnDestroy {
  checklistHeaderFound: ChecklistHeaderFindAllResponse[] = []
  allChecklistHeaders: ChecklistHeaderFindAllResponse[] = []

  checklistHeaderTotal = 0

  checklistHeaderLoader = new BehaviorSubject<boolean>(false)

  companyId!: number
  companyName = ''
  currentUser: User | null = null
  logoBase64: string | null = null
  logoWidth = 0
  logoHeight = 0

  idSearch = ''

  statusFilters: NzTableFilterList = []

  userSearch = ''
  visibleUserFilters: { text: string; value: any; checked: boolean }[] = []
  userFilters: { text: string; value: any; checked: boolean }[] = []

  modelSearch = ''
  visibleModelFilters: { text: string; value: any; checked: boolean }[] = []
  modelFilters: { text: string; value: any; checked: boolean }[] = []

  chassisSearch = ''
  visibleChassisFilters: { text: string; value: any; checked: boolean }[] = []
  chassisFilters: { text: string; value: any; checked: boolean }[] = []

  filterConditions: { [key: string]: any } = {}

  currentPage = 1
  currentPageSize = 10
  filteredChecklistHeaders: ChecklistHeaderFindAllResponse[] = []

  statusCounts = {
    total: 0,
    in_progress: 0,
    done: 0,
    canceled: 0,
    not_proceed: 0,
  }

  constructor(
    private checklistHeaderService: ChecklistHeaderService,
    private notificationService: NzNotificationService,
    private loaderService: LoaderService,
    private headerService: HeaderService,
    private route: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private companyService: CompanyService,
    private imagesService: ImagesService,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      if (params['id']) {
        this.companyId = params['id']
        this.fetchCompanyNameAndSearch()
      }
    })

    // Initial fetch (fallback or default)
    this.handleSearchChecklistHeader();

    this.headerService.registerAction(searchString => {
      return this.handleSearchChecklistHeader(1, 10000, searchString)
    }, 'Buscar projeto')

    this.authService.authenticatedUser.subscribe(user => {
      this.currentUser = user;
    });

    this.loadLogo('assets/images/square-logo.png');
  }

  fetchCompanyNameAndSearch() {
    this.companyService.getById(this.companyId).subscribe({
      next: (company) => {
        this.companyName = company.name;
        this.handleSearchChecklistHeader();

        if (company.logo) {
          this.imagesService.photo(company.logo).subscribe(url => {
            if (url) this.loadLogo(url);
          });
        }
      },
      error: () => {
        this.companyName = 'Empresa Desconhecida';
        this.handleSearchChecklistHeader();
      }
    });
  }

  loadLogo(url: string) {
    const img = new Image();
    img.src = url;
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      this.logoWidth = img.width;
      this.logoHeight = img.height;
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        this.logoBase64 = canvas.toDataURL('image/png');
      }
    };
  }

  ngOnDestroy(): void {
    this.headerService.removeAction()
  }

  handleOpenChecklistHeader(id?: number) {
    if (!id) return
    this.route.navigate([`checklist-headers/${id}`])
  }

  handleSearchChecklistHeader(
    pageIndex: number = 1,
    pageSize: number = 10000,
    searchString?: string,
  ) {
    this.loaderService
      .executeWithLoader(
        this.checklistHeaderService.findAll({
          pageIndex,
          pageSize,
          searchString,
          companyId: this.companyId,
          type: 'maintenance',
        }),
        this.checklistHeaderLoader,
      )
      .subscribe({
        next: ({ items }) => {
          this.allChecklistHeaders = items
          this.refreshStatusCounts()
          this.generateFilters()
          this.applyFilters()
        },
        error: (error: HttpErrorResponse) => {
          this.notificationService.error(
            `${error.status}`,
            error.error?.message || 'Erro ao carregar solicitações.',
          )
        },
      })
  }

  get hasStatusFilter(): boolean {
    const status = this.filterConditions['status']
    return Array.isArray(status) && status.length > 0
  }

  get hasActiveFilters(): boolean {
    return Object.keys(this.filterConditions).some((key) => {
      const value = this.filterConditions[key]
      if (Array.isArray(value)) {
        return value.length > 0 && value.some((item) => item != null && item !== '')
      }
      return value != null && value !== ''
    })
  }

  isStatusFilterActive(status: MaintenanceStatus): boolean {
    const selected = this.filterConditions['status']
    return Array.isArray(selected) && selected.length === 1 && selected[0] === status
  }

  filterByStatus(status: MaintenanceStatus | null): void {
    if (status == null) {
      this.onFilterChange('status', [])
      return
    }

    if (this.isStatusFilterActive(status)) {
      this.onFilterChange('status', [])
      return
    }

    this.onFilterChange('status', [status])
  }

  refreshStatusCounts(): void {
    const counts = {
      total: this.allChecklistHeaders.length,
      in_progress: 0,
      done: 0,
      canceled: 0,
      not_proceed: 0,
    }

    for (const item of this.allChecklistHeaders) {
      if (item.status === 'in_progress') counts.in_progress += 1
      else if (item.status === 'done') counts.done += 1
      else if (item.status === 'canceled') counts.canceled += 1
      else if (item.status === 'not_proceed') counts.not_proceed += 1
    }

    this.statusCounts = counts
  }

  generateFilters() {
    const uniqueModels = new Set(
      this.allChecklistHeaders.map((item) => item.ProductModel?.name || 'N/A'),
    )
    this.modelFilters = Array.from(uniqueModels).map((name) => ({
      text: name,
      value: name,
      checked: false,
    }))
    this.visibleModelFilters = [...this.modelFilters]

    const uniqueChassis = new Set(
      this.allChecklistHeaders.map((item) => item.chassis || 'N/A'),
    )
    this.chassisFilters = Array.from(uniqueChassis).map((chassis) => ({
      text: chassis,
      value: chassis,
      checked: false,
    }))
    this.visibleChassisFilters = [...this.chassisFilters]

    const uniqueStatuses = new Set(
      this.allChecklistHeaders.map((item) => item.status),
    )
    this.statusFilters = Array.from(uniqueStatuses).map((status) => ({
      text: this.setStatusColor(status).text,
      value: status,
    }))

    const uniqueUsers = new Set(
      this.allChecklistHeaders.map((item) => item.UserModel?.name || 'N/A'),
    )
    this.userFilters = Array.from(uniqueUsers).map((name) => ({
      text: name,
      value: name,
      checked: false,
    }))
    this.visibleUserFilters = [...this.userFilters]
  }

  onFilterChange(column: string, value: any) {
    this.filterConditions[column] = value;
    this.applyFilters();
  }

  onUserSearch(searchValue: string) {
    this.userSearch = searchValue;
    this.visibleUserFilters = this.userFilters.filter(item => item.text.toLowerCase().includes(searchValue.toLowerCase()));
  }

  applyUserFilter() {
    const selectedUsers = this.userFilters.filter(item => item.checked).map(item => item.value);
    this.onFilterChange('user', selectedUsers);
  }

  clearUserFilter() {
    this.userSearch = '';
    this.userFilters.forEach(item => item.checked = false);
    this.visibleUserFilters = [...this.userFilters];
    this.onFilterChange('user', []);
  }

  onModelSearch(searchValue: string) {
    this.modelSearch = searchValue;
    this.visibleModelFilters = this.modelFilters.filter(item => item.text.toLowerCase().includes(searchValue.toLowerCase()));
  }

  applyModelFilter() {
    const selectedModels = this.modelFilters.filter(item => item.checked).map(item => item.value);
    this.onFilterChange('model', selectedModels);
  }

  clearModelFilter() {
    this.modelSearch = '';
    this.modelFilters.forEach(item => item.checked = false);
    this.visibleModelFilters = [...this.modelFilters];
    this.onFilterChange('model', []);
  }

  onChassisSearch(searchValue: string) {
    this.chassisSearch = searchValue;
    this.visibleChassisFilters = this.chassisFilters.filter(item => item.text.toLowerCase().includes(searchValue.toLowerCase()));
  }

  applyChassisFilter() {
    const selectedChassis = this.chassisFilters.filter(item => item.checked).map(item => item.value);
    this.onFilterChange('chassis', selectedChassis);
  }

  clearChassisFilter() {
    this.chassisSearch = '';
    this.chassisFilters.forEach(item => item.checked = false);
    this.visibleChassisFilters = [...this.chassisFilters];
    this.onFilterChange('chassis', []);
  }

  clearAllFilters() {
    this.idSearch = ''
    this.filterConditions = {}
    this.generateFilters()
    this.applyFilters()
  }

  applyFilters() {
    let filtered = [...this.allChecklistHeaders]

    for (const column in this.filterConditions) {
      const value = this.filterConditions[column]
      if (value && (Array.isArray(value) ? value.length > 0 : true)) {
        if (column === 'id') {
          filtered = filtered.filter((item) =>
            item.id.toString().includes(value),
          )
        } else if (column === 'model') {
          filtered = filtered.filter((item) =>
            value.includes(item.ProductModel?.name || 'N/A'),
          )
        } else if (column === 'chassis') {
          filtered = filtered.filter((item) => {
            const itemChassis = item.chassis
              ? item.chassis.toUpperCase()
              : 'N/A'
            return value.some(
              (v: string) => v.toUpperCase() === itemChassis,
            )
          })
        } else if (column === 'status') {
          filtered = filtered.filter((item) => value.includes(item.status))
        } else if (column === 'user') {
          filtered = filtered.filter((item) =>
            value.includes(item.UserModel?.name || 'N/A'),
          )
        } else if (column === 'date') {
          if (
            Array.isArray(value) &&
            value.length === 2 &&
            value[0] &&
            value[1]
          ) {
            const startDate = new Date(value[0])
            startDate.setHours(0, 0, 0, 0)

            const endDate = new Date(value[1])
            endDate.setHours(23, 59, 59, 999)

            filtered = filtered.filter((item) => {
              if (!item.date) return false
              const itemDate = new Date(item.date)
              return itemDate >= startDate && itemDate <= endDate
            })
          }
        }
      }
    }

    this.filteredChecklistHeaders = filtered
    this.checklistHeaderTotal = filtered.length
    this.currentPage = 1
    this.updateDisplayedData()
  }

  updateDisplayedData() {
    const startIndex = (this.currentPage - 1) * this.currentPageSize;
    const endIndex = startIndex + this.currentPageSize;
    this.checklistHeaderFound = this.filteredChecklistHeaders.slice(startIndex, endIndex);
  }

  onPageChange(pageIndex: number) {
    this.currentPage = pageIndex;
    this.updateDisplayedData();
  }

  onPageSizeChange(pageSize: number) {
    this.currentPageSize = pageSize;
    this.currentPage = 1;
    this.updateDisplayedData();
  }

  setStatusColor(status: string) {
    switch (status) {
      case 'in_progress':
        return { text: 'Em andamento', color: '#f50' }
      case 'done':
        return { text: 'Finalizado', color: '#87d068' }
      case 'canceled':
        return { text: 'Cancelado', color: '#c0142c' }
      case 'not_proceed':
        return { text: 'Não procede', color: '#cecb13ff' }
      default:
        return { text: 'Desconhecido', color: 'default' }
    }
  }

  onIdSearch(searchValue: string) {
    this.onFilterChange('id', searchValue);
  }

  clearIdFilter() {
    this.idSearch = '';
    this.onFilterChange('id', '');
  }

  exportToPdf() {
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape for better fit

    // --- Global Header ---
    const pageWidth = doc.internal.pageSize.width;
    let currentY = 10;
    const marginX = 14;

    // 1. Logo (Top Right)
    // Max logo size 25x15 (Width x Height)
    const maxLogoWidth = 25;
    const maxLogoHeight = 15;

    if (this.logoBase64) {
      // Calculate aspect ratio
      let finalLogoWidth = maxLogoWidth;
      let finalLogoHeight = maxLogoHeight;

      if (this.logoWidth > 0 && this.logoHeight > 0) {
        const ratio = Math.min(maxLogoWidth / this.logoWidth, maxLogoHeight / this.logoHeight);
        finalLogoWidth = this.logoWidth * ratio;
        finalLogoHeight = this.logoHeight * ratio;
      }

      // Align top-right: x = pageWidth - marginX - finalLogoWidth
      const logoX = pageWidth - marginX - finalLogoWidth;
      doc.addImage(this.logoBase64, 'PNG', logoX, currentY, finalLogoWidth, finalLogoHeight);
    }

    // Header Text Info (Left Aligned)
    const textStartX = marginX;
    let textY = currentY + 5;

    doc.setTextColor(0);

    // 2. Company Name (First Line, Bold, Larger) - REMOVED as per request
    // doc.setFontSize(14);
    // doc.setFont('helvetica', 'bold');
    // doc.text(this.companyName || 'Empresa', textStartX, textY);
    // textY += 6;

    // 3. Report Title
    doc.setFontSize(14); // Increased size since it's now the main title
    doc.setFont('helvetica', 'bold');
    doc.text('Solicitações de Manutenções', textStartX, textY);
    textY += 10; // Increased spacing below title

    // Reset font for other details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    // 4. Date
    const dateStr = new Date().toLocaleString();
    doc.text(`Gerado em: ${dateStr}`, textStartX, textY);
    textY += 5;

    // 5. User
    const userName = this.currentUser ? this.currentUser.name : 'Desconhecido';
    doc.text(`Gerado por: ${userName}`, textStartX, textY);
    textY += 5;

    // 6. Filters
    const activeFilters: string[] = []
    if (this.idSearch) activeFilters.push(`ID: ${this.idSearch}`)

    // Model Filter
    if (this.filterConditions['model']?.length) {
      const selectedModels = this.filterConditions['model'].join(', ')
      activeFilters.push(`Modelo: ${selectedModels}`)
    }

    // Chassis Filter
    if (this.filterConditions['chassis']?.length) {
      const selectedChassis = this.filterConditions['chassis'].join(', ')
      activeFilters.push(`Placa: ${selectedChassis}`)
    }

    // User Filter
    if (this.filterConditions['user']?.length) {
      const selectedUsers = this.filterConditions['user'].join(', ')
      activeFilters.push(`Solicitante: ${selectedUsers}`)
    }

    if (this.filterConditions['status']?.length) {
      const selectedStatuses = this.filterConditions['status']
        .map((s: any) => this.setStatusColor(s).text)
        .join(', ')
      activeFilters.push(`Status: ${selectedStatuses}`)
    }

    // Date filter
    const dateFilter = this.filterConditions['date'];
    if (Array.isArray(dateFilter) && dateFilter.length === 2 && dateFilter[0] && dateFilter[1]) {
      const d1 = new Date(dateFilter[0]).toLocaleDateString();
      const d2 = new Date(dateFilter[1]).toLocaleDateString();
      activeFilters.push(`Data: ${d1} a ${d2}`);
    }

    if (activeFilters.length > 0) {
      const filterText = `Filtros: ${activeFilters.join(', ')}`;
      // Split text if too long. Max width is page width - margin - max logo width - padding
      const maxTextWidth = pageWidth - (marginX * 2) - 35; // 35 = maxLogoWidth (25) + padding (10)
      const splitFilters = doc.splitTextToSize(filterText, maxTextWidth);
      doc.text(splitFilters, textStartX, textY);
      // Adjust Y based on lines
      textY += (splitFilters.length * 5);
    } else {
      doc.text('Filtros: Nenhum', textStartX, textY);
      textY += 5;
    }

    // Ensure table starts below the header
    // Use the max of logo bottom or text bottom
    const headerBottom = Math.max(currentY + 20, textY); // 20 = maxLogoHeight (15) + padding (5)
    const tableStartY = headerBottom + 5;

    const head = [['ID', 'Modelo', 'Placa', 'Status', 'Data', 'Solicitante']]
    const data = this.filteredChecklistHeaders.map((item) => [
      item.id,
      item.ProductModel?.name || 'N/A',
      item.chassis ? item.chassis.toUpperCase() : 'N/A',
      this.setStatusColor(item.status).text,
      item.date ? new Date(item.date).toLocaleDateString() : '',
      item.UserModel?.name || 'N/A',
    ])

    autoTable(doc, {
      startY: tableStartY,
      head: head,
      body: data,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 4,
        valign: 'middle',
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [192, 20, 44],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { cellWidth: 18, halign: 'center' },
        1: { cellWidth: 45 },
        2: { cellWidth: 40 },
        3: { cellWidth: 35, halign: 'center' },
        4: { cellWidth: 28, halign: 'center' },
        5: { cellWidth: 'auto' },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 3) {
          const rowIndex = data.row.index
          const originalItem = this.filteredChecklistHeaders[rowIndex]

          if (originalItem) {
            const statusInfo = this.setStatusColor(originalItem.status)
            data.cell.styles.textColor = statusInfo.color
            data.cell.styles.fontStyle = 'bold'
          }
        }
      },
    })

    doc.save(`Solicitacoes-de-Manutencoes.pdf`);
  }
}
